import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Badge, 
  Form, 
  InputGroup,
  Modal
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as Icons from 'react-icons/fa';
import { contactsAPI } from '../services/api';
import Icon from '../components/Icon';

interface Contact {
  _id: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'read' | 'unread'>('all');

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  // Function to fetch contacts from API
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await contactsAPI.getContacts();
      setContacts(response.data);
    } catch (err: any) {
      setError('Failed to fetch contacts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter contacts based on search term and filter status
  const filteredContacts = contacts.filter(contact => {
    // Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'read' && !contact.isRead) return false;
      if (filterStatus === 'unread' && contact.isRead) return false;
    }
    
    // Search term filter
    return (
      (contact.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (contact.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (contact.subject?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (contact.message?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle contact deletion confirmation
  const confirmDelete = (contact: Contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  // Handle contact deletion
  const handleDelete = async () => {
    if (!contactToDelete) return;
    
    try {
      await contactsAPI.deleteContact(contactToDelete._id);
      setContacts(contacts.filter(c => c._id !== contactToDelete._id));
      setShowDeleteModal(false);
      setContactToDelete(null);
    } catch (err: any) {
      setError('Failed to delete contact');
      console.error(err);
    }
  };

  // Mark contact as read
  const markAsRead = async (id: string) => {
    try {
      await contactsAPI.markContactAsRead(id);
      
      // Update local state
      setContacts(contacts.map(contact => 
        contact._id === id 
          ? { ...contact, isRead: true }
          : contact
      ));
    } catch (err: any) {
      setError('Failed to mark contact as read');
      console.error(err);
    }
  };

  if (loading && contacts.length === 0) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading contact requests...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1 className="mb-4">Contact Requests</h1>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">All Requests</h5>
            </Col>
            <Col md={3}>
              <Form.Select 
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as 'all' | 'read' | 'unread')}
                aria-label="Filter by status"
              >
                <option value="all">All Requests</option>
                <option value="read">Read</option>
                <option value="unread">Unread</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Icon icon={Icons.FaSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Status</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.length > 0 ? (
                filteredContacts.map((contact) => (
                  <tr key={contact._id} className={contact.isRead ? '' : 'table-primary'}>
                    <td>{contact.name || 'Unknown'}</td>
                    <td>
                      <a href={`mailto:${contact.email}`}>{contact.email || 'No email'}</a>
                    </td>
                    <td>{contact.subject || 'No subject'}</td>
                    <td>{formatDate(contact.createdAt)}</td>
                    <td>
                      <Badge bg={contact.isRead ? 'success' : 'danger'}>
                        {contact.isRead ? 'Read' : 'Unread'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex">
                        <Link 
                          to={`/contacts/${contact._id}`} 
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => {
                            if (!contact.isRead) {
                              markAsRead(contact._id);
                            }
                          }}
                        >
                          {contact.isRead ? 
                            <Icon icon={Icons.FaEnvelopeOpen} /> : 
                            <Icon icon={Icons.FaEnvelope} />}
                        </Link>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => confirmDelete(contact)}
                        >
                          <Icon icon={Icons.FaTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-3">
                    {error ? error : 'No contact requests found'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the contact request from "{contactToDelete?.name}"? 
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Contacts; 